import { useState, useEffect, useCallback } from 'react';
import { 
  Eye, 
  GitCompare, 
  ExternalLink, 
  TrendingUp, 
  TrendingDown, 
  Minus,
  Loader2,
  BarChart3,
  Info,
  Lock,
  Sparkles,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  Users,
  Target
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { vendorApi } from '@/services/api';
import { cn } from '@/lib/utils';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart as RechartsPie,
  Pie,
  Cell
} from 'recharts';

const INTEREST_SIGNAL_STYLES = {
  High: 'bg-green-100 text-green-700 border-green-300',
  Moderate: 'bg-amber-100 text-amber-700 border-amber-300',
  Low: 'bg-slate-100 text-slate-600 border-slate-300',
};

const CHART_COLORS = ['#3b82f6', '#f59e0b', '#10b981', '#8b5cf6', '#ec4899', '#06b6d4'];

const TrendIcon = ({ trend, value }) => {
  if (trend === 'up') return (
    <div className="flex items-center gap-1 text-green-600">
      <ArrowUpRight className="w-4 h-4" />
      {value && <span className="text-xs font-medium">+{value}%</span>}
    </div>
  );
  if (trend === 'down') return (
    <div className="flex items-center gap-1 text-red-500">
      <ArrowDownRight className="w-4 h-4" />
      {value && <span className="text-xs font-medium">{value}%</span>}
    </div>
  );
  return <Minus className="w-4 h-4 text-muted-foreground" />;
};

// Mini sparkline component for metric cards
const MiniSparkline = ({ data, dataKey, color = '#3b82f6' }) => {
  if (!data || data.length === 0) return null;
  
  return (
    <div className="w-24 h-8">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data}>
          <defs>
            <linearGradient id={`gradient-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.3} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area 
            type="monotone" 
            dataKey={dataKey} 
            stroke={color} 
            strokeWidth={1.5}
            fill={`url(#gradient-${dataKey})`}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const MetricCard = ({ title, value, icon: Icon, trend, trendValue, tooltip, locked = false, sparklineData, sparklineKey, sparklineColor }) => (
  <Card className={cn(locked && 'opacity-60')}>
    <CardContent className="pt-6">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={cn(
            'w-10 h-10 rounded-lg flex items-center justify-center',
            locked ? 'bg-muted' : 'bg-primary/10'
          )}>
            {locked ? (
              <Lock className="w-5 h-5 text-muted-foreground" />
            ) : (
              <Icon className="w-5 h-5 text-primary" />
            )}
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <p className="text-sm text-muted-foreground">{title}</p>
              {tooltip && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="w-3.5 h-3.5 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p>{tooltip}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            <p className="text-2xl font-semibold">
              {locked ? '—' : value.toLocaleString()}
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          {trend && !locked && <TrendIcon trend={trend} value={trendValue} />}
          {sparklineData && !locked && (
            <MiniSparkline data={sparklineData} dataKey={sparklineKey} color={sparklineColor} />
          )}
        </div>
      </div>
    </CardContent>
  </Card>
);

// Conversion funnel component
const ConversionFunnel = ({ data }) => {
  if (!data) return null;
  
  const steps = [
    { label: 'Wizard Results', value: data.wizard_results || 0, color: '#3b82f6' },
    { label: 'Profile Views', value: data.profile_views || 0, color: '#8b5cf6' },
    { label: 'Comparisons', value: data.comparison_inclusions || 0, color: '#f59e0b' },
    { label: 'Outbound Clicks', value: data.outbound_clicks || 0, color: '#10b981' },
  ].filter(s => s.value > 0);

  if (steps.length < 2) return null;

  const maxValue = Math.max(...steps.map(s => s.value));

  return (
    <div className="space-y-3">
      {steps.map((step, index) => {
        const widthPercent = (step.value / maxValue) * 100;
        const conversionRate = index > 0 && steps[index - 1].value > 0
          ? ((step.value / steps[index - 1].value) * 100).toFixed(1)
          : null;

        return (
          <div key={step.label} className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{step.label}</span>
              <div className="flex items-center gap-2">
                <span className="font-medium">{step.value.toLocaleString()}</span>
                {conversionRate && (
                  <Badge variant="secondary" className="text-xs">
                    {conversionRate}%
                  </Badge>
                )}
              </div>
            </div>
            <div className="h-6 bg-muted rounded-md overflow-hidden">
              <div 
                className="h-full rounded-md transition-all duration-500"
                style={{ 
                  width: `${widthPercent}%`, 
                  backgroundColor: step.color 
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Source breakdown pie chart
const SourceBreakdown = ({ data }) => {
  if (!data || data.length === 0) return null;

  const total = data.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="flex items-center gap-6">
      <div className="w-32 h-32">
        <ResponsiveContainer width="100%" height="100%">
          <RechartsPie>
            <Pie
              data={data}
              innerRadius={35}
              outerRadius={55}
              paddingAngle={2}
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
              ))}
            </Pie>
          </RechartsPie>
        </ResponsiveContainer>
      </div>
      <div className="flex-1 space-y-2">
        {data.map((item, index) => (
          <div key={item.name} className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <div 
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
              />
              <span className="text-muted-foreground">{item.name}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-medium">{item.value.toLocaleString()}</span>
              <span className="text-xs text-muted-foreground">
                ({((item.value / total) * 100).toFixed(0)}%)
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Weekly comparison bar chart
const WeeklyComparison = ({ data }) => {
  if (!data || data.length === 0) return null;

  return (
    <div className="h-[200px]">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} barGap={8}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-muted" />
          <XAxis 
            dataKey="week" 
            tick={{ fontSize: 11 }}
            className="text-muted-foreground"
            axisLine={false}
          />
          <YAxis 
            tick={{ fontSize: 11 }}
            className="text-muted-foreground"
            axisLine={false}
          />
          <RechartsTooltip 
            contentStyle={{ 
              borderRadius: '8px', 
              border: '1px solid hsl(var(--border))',
              background: 'hsl(var(--background))',
              fontSize: '12px'
            }}
          />
          <Bar dataKey="views" name="Views" fill="#3b82f6" radius={[4, 4, 0, 0]} />
          <Bar dataKey="comparisons" name="Comparisons" fill="#f59e0b" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export function VendorAnalytics({ vendor, platforms = [], selectedPlatformId }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState(30);
  const [platformView, setPlatformView] = useState(selectedPlatformId || 'all');
  const [summary, setSummary] = useState(null);
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  const isEnterprise = vendor?.tier?.toLowerCase() === 'enterprise';
  const canViewAnalytics = vendor?.permissions?.can_view_analytics;
  const canAggregate = isEnterprise;

  // Derived data for visualizations
  const sourceBreakdown = summary ? [
    { name: 'Wizard Results', value: summary.wizard_results || summary.profile_views || 0 },
    { name: 'Direct Search', value: Math.round((summary.profile_views || 0) * 0.3) },
    { name: 'Comparisons', value: summary.comparison_inclusions || 0 },
  ].filter(s => s.value > 0) : [];

  // Calculate weekly data from time series
  const weeklyData = timeSeriesData.length > 0 ? (() => {
    const weeks = [];
    for (let i = 0; i < timeSeriesData.length; i += 7) {
      const weekData = timeSeriesData.slice(i, i + 7);
      if (weekData.length > 0) {
        weeks.push({
          week: `W${Math.floor(i / 7) + 1}`,
          views: weekData.reduce((sum, d) => sum + (d.profile_views || 0), 0),
          comparisons: weekData.reduce((sum, d) => sum + (d.comparison_inclusions || 0), 0),
        });
      }
    }
    return weeks.slice(-4); // Last 4 weeks
  })() : [];

  // Get last 7 days for sparklines
  const recentData = timeSeriesData.slice(-7);

  // Update platformView when selectedPlatformId changes
  useEffect(() => {
    if (selectedPlatformId && !canAggregate) {
      setPlatformView(selectedPlatformId);
    }
  }, [selectedPlatformId, canAggregate]);

  const fetchAnalytics = useCallback(async () => {
    if (!canViewAnalytics) return;
    
    // For non-Enterprise, force to selected platform
    const queryPlatformId = canAggregate ? platformView : (selectedPlatformId || platforms[0]?.id || 'all');
    
    setLoading(true);
    setError(null);
    try {
      const [summaryData, viewsData] = await Promise.all([
        vendorApi.getAnalyticsSummary(period, queryPlatformId),
        vendorApi.getAnalyticsViews(period, queryPlatformId)
      ]);
      setSummary(summaryData);
      setTimeSeriesData(viewsData.data || []);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [period, platformView, canViewAnalytics, canAggregate, selectedPlatformId, platforms]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // No access - show upgrade prompt
  if (!canViewAnalytics) {
    return (
      <Card className="py-12">
        <CardContent className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-muted rounded-full flex items-center justify-center">
            <Lock className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold mb-2">Analytics Unavailable</h3>
          <p className="text-muted-foreground max-w-md mx-auto mb-4">
            Engagement analytics are available for Verified and Enterprise partners.
            Upgrade your tier to see how users discover and engage with your platform.
          </p>
          <Badge variant="outline" className="text-primary border-primary">
            Verified Partner or higher required
          </Badge>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="py-8">
        <CardContent className="text-center">
          <p className="text-destructive mb-4">{error}</p>
          <Button variant="outline" onClick={fetchAnalytics}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Format date for chart
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  // Get current platform name for display
  const currentPlatformName = platformView === 'all' 
    ? 'All Platforms' 
    : platforms.find(p => p.id === platformView)?.name || 'Selected Platform';

  return (
    <div className="space-y-6">
      {/* Header with platform view toggle and period selector */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Engagement Overview
          </h3>
          <p className="text-sm text-muted-foreground">
            {platformView === 'all' 
              ? 'Aggregated visibility insights across all your platforms'
              : `Visibility insights for ${currentPlatformName}`
            }
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Platform View Toggle (Enterprise only) */}
          {canAggregate && platforms.length > 1 && (
            <Select value={platformView} onValueChange={setPlatformView}>
              <SelectTrigger className="w-[160px]" data-testid="platform-view-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Platforms</SelectItem>
                {platforms.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          {/* Period Selector */}
          {isEnterprise ? (
            <Select value={period.toString()} onValueChange={(v) => setPeriod(parseInt(v))}>
              <SelectTrigger className="w-[140px]" data-testid="period-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <Badge variant="secondary">Last 30 days</Badge>
          )}
        </div>
      </div>

      {/* Interest Signal (Derived Metric) */}
      {summary?.interest_signal && (
        <Card className="border-2 border-primary/10">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-amber-500" />
                <div>
                  <p className="text-sm text-muted-foreground">Interest Signal</p>
                  <p className="text-sm">Based on views and comparison activity</p>
                </div>
              </div>
              <Badge 
                variant="outline" 
                className={cn('text-sm font-medium', INTEREST_SIGNAL_STYLES[summary.interest_signal])}
              >
                {summary.interest_signal}
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Metric Cards with Sparklines */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <MetricCard
          title="Profile Views"
          value={summary?.profile_views || 0}
          icon={Eye}
          trend={summary?.trends?.views}
          trendValue={summary?.trend_percentages?.views}
          tooltip="Number of times users viewed your platform detail page"
          sparklineData={recentData}
          sparklineKey="profile_views"
          sparklineColor="#3b82f6"
        />
        <MetricCard
          title="Comparison Inclusions"
          value={summary?.comparison_inclusions || 0}
          icon={GitCompare}
          trend={summary?.trends?.comparisons}
          trendValue={summary?.trend_percentages?.comparisons}
          tooltip="Times your platform appeared in wizard results or side-by-side comparisons"
          sparklineData={recentData}
          sparklineKey="comparison_inclusions"
          sparklineColor="#f59e0b"
        />
        <MetricCard
          title="Outbound Clicks"
          value={summary?.outbound_clicks || 0}
          icon={ExternalLink}
          trend={summary?.trends?.clicks}
          trendValue={summary?.trend_percentages?.clicks}
          tooltip="Clicks to your website, documentation, or demo links"
          locked={!summary?.has_click_access}
          sparklineData={recentData}
          sparklineKey="outbound_clicks"
          sparklineColor="#10b981"
        />
      </div>

      {/* Tabbed Analytics Views */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-grid">
          <TabsTrigger value="overview" className="gap-2">
            <BarChart3 className="w-4 h-4" />
            <span className="hidden sm:inline">Timeline</span>
          </TabsTrigger>
          <TabsTrigger value="funnel" className="gap-2">
            <Target className="w-4 h-4" />
            <span className="hidden sm:inline">Funnel</span>
          </TabsTrigger>
          <TabsTrigger value="breakdown" className="gap-2">
            <PieChart className="w-4 h-4" />
            <span className="hidden sm:inline">Breakdown</span>
          </TabsTrigger>
        </TabsList>

        {/* Timeline Tab */}
        <TabsContent value="overview" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Visibility Over Time</CardTitle>
              <CardDescription>Daily engagement metrics for the selected period</CardDescription>
            </CardHeader>
            <CardContent>
              {timeSeriesData.length > 0 ? (
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={timeSeriesData}>
                      <defs>
                        <linearGradient id="viewsGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
                          <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="comparisonsGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.3} />
                          <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="clicksGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
                          <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" vertical={false} />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={formatDate}
                        tick={{ fontSize: 11 }}
                        className="text-muted-foreground"
                        axisLine={false}
                      />
                      <YAxis 
                        tick={{ fontSize: 11 }}
                        className="text-muted-foreground"
                        axisLine={false}
                      />
                      <RechartsTooltip 
                        labelFormatter={formatDate}
                        contentStyle={{ 
                          borderRadius: '8px', 
                          border: '1px solid hsl(var(--border))',
                          background: 'hsl(var(--background))',
                          fontSize: '12px'
                        }}
                      />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="profile_views" 
                        name="Profile Views"
                        stroke="#3b82f6" 
                        strokeWidth={2}
                        fill="url(#viewsGradient)"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="comparison_inclusions" 
                        name="Comparisons"
                        stroke="#f59e0b" 
                        strokeWidth={2}
                        fill="url(#comparisonsGradient)"
                      />
                      {summary?.has_click_access && (
                        <Area 
                          type="monotone" 
                          dataKey="outbound_clicks" 
                          name="Outbound Clicks"
                          stroke="#10b981" 
                          strokeWidth={2}
                          fill="url(#clicksGradient)"
                        />
                      )}
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                  <p>No data available for this period</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Funnel Tab */}
        <TabsContent value="funnel" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Target className="w-4 h-4" />
                Engagement Funnel
              </CardTitle>
              <CardDescription>
                User journey from discovery to action
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ConversionFunnel data={summary} />
              {!summary && (
                <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                  <p>No funnel data available</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Breakdown Tab */}
        <TabsContent value="breakdown" className="mt-4">
          <div className="grid lg:grid-cols-2 gap-4">
            {/* Source Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <PieChart className="w-4 h-4" />
                  Traffic Sources
                </CardTitle>
                <CardDescription>
                  Where your visibility comes from
                </CardDescription>
              </CardHeader>
              <CardContent>
                {sourceBreakdown.length > 0 ? (
                  <SourceBreakdown data={sourceBreakdown} />
                ) : (
                  <div className="h-[150px] flex items-center justify-center text-muted-foreground">
                    <p>No source data available</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Weekly Comparison */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Weekly Trends
                </CardTitle>
                <CardDescription>
                  Engagement by week
                </CardDescription>
              </CardHeader>
              <CardContent>
                {weeklyData.length > 0 ? (
                  <WeeklyComparison data={weeklyData} />
                ) : (
                  <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                    <p>Not enough data for weekly comparison</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Disclaimer */}
      <p className="text-xs text-muted-foreground text-center px-4">
        Analytics show aggregated engagement and visibility metrics. 
        This does not represent leads, user identity, or personal data.
      </p>

      {/* Upgrade prompt for Verified tier */}
      {!isEnterprise && (
        <Card className="bg-gradient-to-r from-amber-50 to-amber-100 dark:from-amber-950/20 dark:to-amber-900/20 border-amber-200">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800 dark:text-amber-200">
                  Unlock Full Analytics Suite
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                  Enterprise partners get outbound click tracking, trend indicators, 
                  custom date ranges (7/30/90 days), and advanced visualizations.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
